import React, { useState, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ChevronRight, ChevronDown, Info, Lightbulb, Users, Plus, Trash2, X, Pencil, Loader2, AlertTriangle } from 'lucide-react';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { Toast } from '../components/Toast';
type Step = 1 | 2 | 3 | 4 | 5;
type Audience = 'everyone' | 'subscribers' | 'tag_based' | 'opt_in' | 'hybrid';
type TierMode = 'template' | 'custom' | 'single';
type CalcMethod = 'annual' | 'lifetime' | 'points';
type DowngradePolicy = 'permanent' | 'reevaluate';
type EvalCadence = 'daily' | 'monthly';
type QualificationWindow = 'rolling' | 'calendar' | 'financial' | 'custom';
interface CustomTier {
  id: string;
  name: string;
  color: string;
  threshold: number;
  multiplier: number;
  benefits: string[];
}
const SWATCH_COLORS: {
  name: string;
  hex: string;
}[] = [{
  name: 'Burgundy',
  hex: '#722F37'
}, {
  name: 'Forest',
  hex: '#2C5F2D'
}, {
  name: 'Navy',
  hex: '#1A3A5C'
}, {
  name: 'Slate',
  hex: '#475569'
}, {
  name: 'Plum',
  hex: '#6B2D5C'
}, {
  name: 'Gold',
  hex: '#B08D57'
}];
const CALC_METHODS: {
  id: CalcMethod;
  label: string;
  note?: string;
  desc?: string;
}[] = [{
  id: 'annual',
  label: 'Annual Spend',
  note: 'most common'
}, {
  id: 'lifetime',
  label: 'Lifetime Spend'
}, {
  id: 'points',
  label: 'Points earned',
  desc: 'Tier is based on the points a member has earned over time, not their current spendable balance — redeeming rewards or points expiring never demotes anyone.'
}];
// Windows only apply to Annual Spend — the period spend is measured over.
const QUALIFICATION_WINDOWS: {
  id: QualificationWindow;
  title: string;
  desc: string;
}[] = [{
  id: 'rolling',
  title: 'Rolling 12 months',
  desc: 'The trailing 12 months from the evaluation date.'
}, {
  id: 'calendar',
  title: 'Calendar year',
  desc: '1 January to 31 December.'
}, {
  id: 'financial',
  title: 'Financial year',
  desc: '1 July to 30 June — common in Australia and New Zealand.'
}, {
  id: 'custom',
  title: 'Custom start month',
  desc: 'Set your own 12-month window start.'
}];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const TEMPLATE_TIERS: CustomTier[] = [{
  id: 't1',
  name: 'Enthusiast',
  color: '#2C5F2D',
  threshold: 0,
  multiplier: 1,
  benefits: []
}, {
  id: 't2',
  name: 'Connoisseur',
  color: '#1A3A5C',
  threshold: 500,
  multiplier: 1.1,
  benefits: ['Early access']
}, {
  id: 't3',
  name: 'Sommelier',
  color: '#6B2D5C',
  threshold: 2000,
  multiplier: 1.25,
  benefits: ['Priority support']
}, {
  id: 't4',
  name: 'Vintner',
  color: '#B08D57',
  threshold: 5000,
  multiplier: 1.5,
  benefits: ['Exclusive events']
}];
const STEPS: {
  num: Step;
  label: string;
}[] = [{
  num: 1,
  label: 'Program Basics'
}, {
  num: 2,
  label: 'Earning'
}, {
  num: 3,
  label: 'Tiers'
}, {
  num: 4,
  label: 'Review'
}, {
  num: 5,
  label: 'Launch'
}];
const MAX_NAME_LENGTH = 60;
export function LoyaltySetupWizardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Resume mode: any bare visit to /loyalty/setup (without ?fresh=true) is treated
  // as resuming a saved draft. The "First Time" redirect appends ?fresh=true so the
  // wizard starts blank.
  const isFreshStart = searchParams.get('fresh') === 'true';
  const [isResumed, setIsResumed] = useState<boolean>(!isFreshStart);
  const [currentStep, setCurrentStep] = useState<Step>(!isFreshStart ? 3 : 1);
  // Step 1
  const [programName, setProgramName] = useState(!isFreshStart ? 'Acme Wine Rewards Draft' : '');
  const [audience, setAudience] = useState<Audience>('hybrid');
  // Step 2
  const [pointsPerDollar, setPointsPerDollar] = useState<number>(5);
  const [redemptionValue, setRedemptionValue] = useState<number>(0.01);
  const [minRedemption, setMinRedemption] = useState<number>(500);
  const [showEarningExplanation, setShowEarningExplanation] = useState(false);
  // Step 3
  // A flat points program is a single tier rather than tiers switched off.
  const [singleTier, setSingleTier] = useState<CustomTier>({
    id: 'single',
    name: 'Member',
    color: '#722F37',
    threshold: 0,
    multiplier: 1,
    benefits: []
  });
  const [tierMode, setTierMode] = useState<TierMode>('template');
  const [customTiers, setCustomTiers] = useState<CustomTier[]>(TEMPLATE_TIERS);
  const [calcMethod, setCalcMethod] = useState<CalcMethod>('points');
  const [downgradePolicy, setDowngradePolicy] = useState<DowngradePolicy>('permanent');
  const [evalCadence, setEvalCadence] = useState<EvalCadence>('daily');
  const [qualificationWindow, setQualificationWindow] = useState<QualificationWindow>('rolling');
  const [customStartMonth, setCustomStartMonth] = useState('July');
  // Fixed windows close on a date, so downgrades are assessed at period close.
  const isFixedWindow = calcMethod === 'annual' && qualificationWindow !== 'rolling';
  // Tier multipliers compound the base rate, so the true give-back is the base
  // rebate times the highest multiplier in play.
  const isSingleTierMode = tierMode === 'single';
  const activeTiers = tierMode === 'template' ? TEMPLATE_TIERS : isSingleTierMode ? [singleTier] : customTiers;
  const topMultiplier = activeTiers.reduce((max, tier) => Math.max(max, tier.multiplier || 1), 1);
  const baseRebate = pointsPerDollar * redemptionValue * 100;
  const topTierRebate = baseRebate * topMultiplier;
  const isRebateHigh = topTierRebate > 12;
  const windowLabel = QUALIFICATION_WINDOWS.find(w => w.id === qualificationWindow)?.title ?? '';
  const customWindowEnd = MONTHS[(MONTHS.indexOf(customStartMonth) + 11) % 12];
  const windowExplainer = qualificationWindow === 'rolling' ? 'Members are evaluated on their spend in the trailing twelve months from the evaluation date.' : qualificationWindow === 'calendar' ? 'Members are evaluated on their spend in the calendar year, 1 January to 31 December.' : qualificationWindow === 'financial' ? 'Members are evaluated on their spend in the financial year, 1 July to 30 June.' : `Members are evaluated on their spend in the twelve months from 1 ${customStartMonth} to the end of ${customWindowEnd}.`;
  const [benefitDrafts, setBenefitDrafts] = useState<Record<string, string>>({});
  // Step 5
  const [launchMode, setLaunchMode] = useState<'test' | 'live'>('test');
  // UI state
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [toast, setToast] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [isLaunching, setIsLaunching] = useState(false);
  // --- Validation per step ---
  const canProceed = (): boolean => {
    if (currentStep === 1) return programName.trim().length > 0;
    if (currentStep === 2) return pointsPerDollar > 0 && redemptionValue > 0 && minRedemption > 0;
    return true;
  };
  // --- Handlers ---
  const handleNext = () => {
    if (currentStep < 5 && canProceed()) {
      setCurrentStep(prev => prev + 1 as Step);
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1 as Step);
    }
  };
  const handleSaveDraft = () => {
    setToast({
      id: `draft-${Date.now()}`,
      title: 'Draft saved'
    });
  };
  const handleCreateProgram = () => {
    setIsLaunching(true);
    // Simulate API call
    setTimeout(() => {
      setIsLaunching(false);
      if (launchMode === 'test') {
        setToast({
          id: `created-${Date.now()}`,
          title: "Your loyalty program is set up. Tag customers with 'loyalty-test' to start piloting."
        });
        setTimeout(() => navigate('/loyalty?status=test'), 600);
      } else {
        setToast({
          id: `created-${Date.now()}`,
          title: 'Your loyalty program is live!'
        });
        setTimeout(() => navigate('/loyalty'), 600);
      }
    }, 1200);
  };
  const handleDiscard = () => {
    setShowCancelConfirm(false);
    navigate('/');
  };
  const handleStartOver = () => {
    setIsResumed(false);
    setCurrentStep(1);
    setProgramName('');
    setAudience('hybrid');
    setPointsPerDollar(5);
    setRedemptionValue(0.01);
    setMinRedemption(500);
    setShowEarningExplanation(false);
    setTierMode('template');
    setCustomTiers(TEMPLATE_TIERS);
    setCalcMethod('points');
    setQualificationWindow('rolling');
    setCustomStartMonth('July');
    setDowngradePolicy('permanent');
    setEvalCadence('daily');
    setBenefitDrafts({});
    setLaunchMode('test');
    // Reflect fresh state in URL
    setSearchParams({
      fresh: 'true'
    }, {
      replace: true
    });
  };
  // --- Stepper ---
  const renderStepper = () => <div className="flex items-center justify-center max-w-3xl mx-auto" data-id="element-10620" data-mp="284m476kidwgs">
      {STEPS.map((step, index) => {
      const isActive = currentStep === step.num;
      const isCompleted = currentStep > step.num;
      return <Fragment key={step.num} data-id="element-10621" data-mp="1g28nyidmjix" data-mpi={index}>
            <div className="flex flex-col items-center relative z-10" data-id="element-10622" data-mp="3skagz2uund1u">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-semibold transition-all duration-200 ${isCompleted ? 'bg-[#10B981] text-white shadow-[0_0_0_3px_rgba(16,185,129,0.15)]' : isActive ? 'bg-[#0EA5E9] text-white shadow-[0_0_0_4px_rgba(14,165,233,0.15)]' : 'bg-[#F1F5F9] text-[#94A3B8] border border-[#E2E8F0]'}`} data-id="element-10623" data-mp="3nttzn7nwr7a7">
                {isCompleted ? <Check className="w-4 h-4" strokeWidth={2.5} data-id="element-10624" data-mp="39w5ioawyokaf" /> : step.num}
              </div>
              <span className={`mt-2 text-[12px] whitespace-nowrap ${isActive ? 'font-semibold text-[#0F172A]' : isCompleted ? 'font-medium text-[#475569]' : 'font-normal text-[#94A3B8]'}`} data-id="element-10625" data-mp="34x2bokxf83zt">
                {step.num}. {step.label}
              </span>
            </div>

            {index < STEPS.length - 1 && <div className="flex-1 mx-3 mt-[-18px]" data-id="element-10626" data-mp="y2fbbjfmrfvj">
                <div className={`h-[2px] w-full rounded-full transition-colors duration-200 ${currentStep > step.num + 1 ? 'bg-[#10B981]' : currentStep > step.num ? 'bg-[#0EA5E9]' : 'bg-[#E2E8F0]'}`} data-id="element-10627" data-mp="2ybpf9zhtixjf" />
              </div>}
          </Fragment>;
    })}
    </div>;
  // --- Step 1: Program Basics (Name + Audience) ---
  const AUDIENCE_CARDS: {
    id: Audience;
    title: string;
    description: string;
    bullets: {
      icon: '✓' | '⚠';
      text: string;
    }[];
    recommended?: boolean;
    footerNote?: string;
  }[] = [{
    id: 'everyone',
    title: 'Auto-enrol everyone',
    description: 'Every customer joins the program automatically.',
    bullets: [{
      icon: '✓',
      text: 'Maximum engagement'
    }, {
      icon: '⚠',
      text: 'Subscribers will receive loyalty on top of their club discount'
    }]
  }, {
    id: 'subscribers',
    title: 'Subscribers only',
    description: 'Only active subscription members are enrolled.',
    bullets: [{
      icon: '✓',
      text: 'Concentrates rewards on the club'
    }, {
      icon: '⚠',
      text: 'One-off shoppers get nothing, so there is no incentive for them to return'
    }]
  }, {
    id: 'tag_based',
    title: 'Tag-based',
    description: 'Enrol customers who carry a specific tag.',
    bullets: [],
    footerNote: "You'll configure which tags include or exclude customers from the Rules page after setup."
  }, {
    id: 'opt_in',
    title: 'Opt-in only',
    description: 'Customers join only when they opt in themselves.',
    bullets: [{
      icon: '✓',
      text: 'Smaller but more engaged member base'
    }, {
      icon: '⚠',
      text: 'Needs at least one opt-in surface switched on in the storefront or customer portal'
    }]
  }, {
    id: 'hybrid',
    title: 'Hybrid',
    description: 'Auto-enrol one group and let everyone else opt in.',
    bullets: [{
      icon: '✓',
      text: 'Covers the club automatically while still letting one-off shoppers join'
    }],
    recommended: true
  }];
  const renderStep1 = () => {
    const charCount = programName.length;
    return <div className="max-w-[600px] mx-auto space-y-8" data-id="element-10628" data-mp="2hstllkvb1vwg">
        <div className="text-center" data-id="element-10629" data-mp="2htd5h74imud4">
          <h2 className="text-2xl font-bold text-[#0F172A] m-0" data-id="element-10630" data-mp="2d1lgh110cwp3">
            Let's set up your loyalty program
          </h2>
          <p className="text-sm text-[#64748B] mt-2 m-0" data-id="element-10631" data-mp="281qpyyl5a94k">
            Start with the basics.
          </p>
        </div>

        {/* Section A — Program Name */}
        <section className="space-y-4" data-id="element-10632" data-mp="1z23mm78uut8v">
          <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider m-0" data-id="element-10633" data-mp="1z10z89aqr8xp">
            Program Name
          </h3>
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm space-y-2" data-id="element-10634" data-mp="1u53fdt09rkg6">
            <label htmlFor="programName" className="block text-sm font-medium text-[#0F172A]" data-id="element-10635" data-mp="1p60l93ltuymz">
              Program Name <span className="text-[#EF4444]" data-id="element-10636" data-mp="3oenw9tva9i87">*</span>
            </label>
            <input id="programName" type="text" value={programName} maxLength={MAX_NAME_LENGTH} onChange={e => setProgramName(e.target.value)} placeholder="e.g. Acme Wine Rewards" className="w-full h-11 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10637" data-mp="3jigmtr3oholi" />
            <div className="flex items-center justify-end" data-id="element-10638" data-mp="9kovtsg33diy">
              <span className={`text-xs ${charCount > MAX_NAME_LENGTH - 10 ? 'text-[#F59E0B]' : 'text-[#94A3B8]'}`} data-id="element-10639" data-mp="9k6o9znz1cvq">
                {charCount} / {MAX_NAME_LENGTH}
              </span>
            </div>
          </div>

          <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-4 flex items-start gap-3" data-id="element-10640" data-mp="3d3i3xpq3fa5q">
            <Lightbulb className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-10641" data-mp="3i34f7tmd2gbw" />
            <p className="text-sm text-[#92400E] m-0" data-id="element-10642" data-mp="3mxrx39mv0chi">
              Most wineries use their brand name + "Rewards" or "Club" (e.g.
              "Yarra Valley Rewards")
            </p>
          </div>
        </section>

        {/* Section B — Audience */}
        <section className="space-y-4" data-id="element-10643" data-mp="zu77swoah608">
          <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider m-0" data-id="element-10644" data-mp="zx1p4fc0aapf">
            Who participates?
          </h3>
          <div className="space-y-3" data-id="element-10645" data-mp="19sv7wggbj0fn">
            {AUDIENCE_CARDS.map(card => {
            const isSelected = audience === card.id;
            return <button key={card.id} type="button" onClick={() => setAudience(card.id)} className={`w-full text-left rounded-lg border p-5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/30 ${isSelected ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1] hover:shadow-sm'}`} data-id="element-10646" data-mp="14sh111qgdwj7">
                  <div className="flex items-start gap-3" data-id="element-10647" data-mp="1nk1jvc0saqjf">
                    <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'border-[#0EA5E9] bg-[#0EA5E9]' : 'border-[#CBD5E1] bg-white'}`} data-id="element-10648" data-mp="1nmw0stkl9t83">
                      {isSelected && <div className="w-2 h-2 rounded-full bg-white" data-id="element-10649" data-mp="2h3znz99co3zh" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-10650" data-mp="2l6mfta60towo">
                      <div className="flex items-center gap-2 mb-1" data-id="element-10651" data-mp="2l40aziailni5">
                        <h4 className="text-base font-semibold text-[#0F172A] m-0" data-id="element-10652" data-mp="275jsku3ai8k6">
                          {card.title}
                        </h4>
                        {card.recommended && <span className="px-2 py-0.5 text-[11px] font-semibold text-[#0369A1] bg-[#E0F2FE] rounded-full" data-id="element-10653" data-mp="1xg085jzxi6n7">
                            Recommended
                          </span>}
                      </div>
                      <p className="text-sm text-[#64748B] m-0 mb-3" data-id="element-10654" data-mp="1ogwsyr3vubdz">
                        {card.description}
                      </p>
                      {card.bullets.length > 0 && <ul className="space-y-1.5 m-0 list-none p-0" data-id="element-10655" data-mp="1ehxshvt9tm22">
                          {card.bullets.map((b, i) => <li key={i} className="flex items-start gap-2 text-sm" data-id="element-10656" data-mp="1jghaxm8nofpo" data-mpi={i}>
                              <span className={b.icon === '✓' ? 'text-[#10B981] font-bold flex-shrink-0' : 'text-[#F59E0B] font-bold flex-shrink-0'} data-id="element-10657" data-mp="10c5kap5j95md">
                                {b.icon}
                              </span>
                              <span className="text-[#475569]" data-id="element-10658" data-mp="qljajbkhz9bl">{b.text}</span>
                            </li>)}
                        </ul>}
                      {card.footerNote && <div className="mt-3 flex items-start gap-2 text-xs text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-2.5" data-id="element-10659" data-mp="lleqjbkdrwgm">
                          <Info className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-10660" data-mp="1ecyfzd7km6r2" />
                          <span data-id="element-10661" data-mp="19c0y71qh3eq3">{card.footerNote}</span>
                        </div>}
                    </div>
                  </div>
                </button>;
          })}
          </div>
        </section>
      </div>;
  };
  // --- Step 2: Earning ---
  const renderStep2 = () => {
    const exampleOrderValue = 50;
    const exampleEarn = Math.round(pointsPerDollar * exampleOrderValue);
    const exampleWorth = exampleEarn * redemptionValue;
    return <div className="max-w-[700px] mx-auto space-y-6" data-id="element-10662" data-mp="24nqo3132ixcc">
        <div className="text-center" data-id="element-10663" data-mp="29k8vk8ps99sd">
          <h2 className="text-2xl font-bold text-[#0F172A] m-0" data-id="element-10664" data-mp="29la4a6jbtk6x">
            How do points work?
          </h2>
          <p className="text-sm text-[#64748B] mt-2 m-0" data-id="element-10665" data-mp="2eiab4jo02qz3">
            Set the base economy. You can adjust per-product rules later from
            the Rules page.
          </p>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm space-y-5" data-id="element-10666" data-mp="2obzjrl3tllgl">
          {/* Row 1: two columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-10667" data-mp="2xaur50ilp1lh">
            <div className="space-y-1.5" data-id="element-10668" data-mp="2x8i80k13wl6u">
              <label htmlFor="pointsPerDollar" className="block text-sm font-medium text-[#0F172A]" data-id="element-10669" data-mp="32a7ju1w5l2vz">
                Points per dollar spent{' '}
                <span className="text-[#EF4444]" data-id="element-10670" data-mp="12yela0tpvph5">*</span>
              </label>
              <div className="relative" data-id="element-10671" data-mp="17wg6arapdyv9">
                <input id="pointsPerDollar" type="number" min={1} step={1} value={pointsPerDollar} onChange={e => setPointsPerDollar(Math.max(0, Number(e.target.value)))} className="w-full h-11 pl-3 pr-16 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10672" data-mp="17xhpjj1vhro2" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8] pointer-events-none" data-id="element-10673" data-mp="3ekylzvwj3vux">
                  pts/$
                </span>
              </div>
            </div>

            <div className="space-y-1.5" data-id="element-10674" data-mp="2lwb6kgygo0pa">
              <label htmlFor="redemptionValue" className="block text-sm font-medium text-[#0F172A]" data-id="element-10675" data-mp="2lxmaqhvtjagx">
                Point value at redemption{' '}
                <span className="text-[#EF4444]" data-id="element-10676" data-mp="2vse7xklhnvb6">*</span>
              </label>
              <div className="relative" data-id="element-10677" data-mp="g21jg2av7ypt">
                <input id="redemptionValue" type="number" min={0} step={0.01} value={redemptionValue} onChange={e => setRedemptionValue(Math.max(0, Number(e.target.value)))} className="w-full h-11 pl-3 pr-16 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10678" data-mp="g0qbrreau6tf" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8] pointer-events-none" data-id="element-10679" data-mp="9m1cmvv613aw">
                  $/pt
                </span>
              </div>
            </div>
          </div>

          {/* Row 2: minimum redemption */}
          <div className="space-y-1.5" data-id="element-10680" data-mp="11dzaotryhatm">
            <label htmlFor="minRedemption" className="block text-sm font-medium text-[#0F172A]" data-id="element-10681" data-mp="16c0ujgx7he65">
              Minimum redemption <span className="text-[#EF4444]" data-id="element-10682" data-mp="slozb95bqzas">*</span>
            </label>
            <div className="relative" data-id="element-10683" data-mp="wmh8y3i3z67l">
              <input id="minRedemption" type="number" min={1} step={1} value={minRedemption} onChange={e => setMinRedemption(Math.max(0, Math.floor(Number(e.target.value))))} className="w-full h-11 pl-3 pr-16 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10684" data-mp="wlfnd53gv1bm" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8] pointer-events-none" data-id="element-10685" data-mp="3gq8mdks31w6n">
                points
              </span>
            </div>
            <p className="text-xs text-[#64748B] m-0" data-id="element-10686" data-mp="36xt4engsd7lr">
              Customers must redeem at least this many points at once. Prevents
              tiny redemptions.
            </p>
          </div>

          {/* Live preview */}
          <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-md px-4 py-3 space-y-3" data-id="element-10687" data-mp="32whnd6bz9up6">
            <p className="text-sm text-[#0369A1] m-0 leading-relaxed" data-id="element-10688" data-mp="2nz8g136a4gbt">
              <span className="font-semibold" data-id="element-10689" data-mp="2o11moigw70jt">Example:</span> a $
              {exampleOrderValue} order earns{' '}
              <span className="font-semibold" data-id="element-10690" data-mp="2t04to8gfnguf">
                {exampleEarn.toLocaleString()}
              </span>{' '}
              points (worth{' '}
              <span className="font-semibold" data-id="element-10691" data-mp="r5vss0m6guck">${exampleWorth.toFixed(2)}</span>{' '}
              at redemption).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-10692" data-mp="2bp903tdm28qh">
              <div className="bg-white border border-[#BAE6FD] rounded-md px-3 py-2" data-id="element-10693" data-mp="26qxq9inbfy89">
                <div className="text-xs font-medium text-[#0369A1] uppercase tracking-wider" data-id="element-10694" data-mp="2lf2air20oht5">
                  Base rebate
                </div>
                <div className="text-lg font-semibold text-[#0F172A] mt-0.5" data-id="element-10695" data-mp="2glzemc9row9s">
                  {baseRebate.toFixed(1)}%
                </div>
                <div className="text-xs text-[#64748B]" data-id="element-10696" data-mp="2qeey5e0ppsws">
                  Every member at 1× points
                </div>
              </div>
              <div className="bg-white border border-[#BAE6FD] rounded-md px-3 py-2" data-id="element-10697" data-mp="34d4x6z73e2ns">
                <div className="text-xs font-medium text-[#0369A1] uppercase tracking-wider" data-id="element-10698" data-mp="2zfv7rjjpdqzy">
                  Top tier rebate
                </div>
                <div className="text-lg font-semibold text-[#0F172A] mt-0.5" data-id="element-10699" data-mp="150iyig07cxzs">
                  {topTierRebate.toFixed(1)}%
                </div>
                <div className="text-xs text-[#64748B]" data-id="element-10700" data-mp="3543k2pmtrcke">
                  Assumes {topMultiplier}× top tier multiplier
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Helper callout */}
        <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-4 flex items-start gap-3" data-id="element-10701" data-mp="3e5tb3ucso9hn">
          <Lightbulb className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-10702" data-mp="3j072lloc5wh1" />
          <p className="text-sm text-[#92400E] m-0" data-id="element-10703" data-mp="3o1e9zjmkgtn6">
            Most wineries set earning between 5 and 20 points per dollar, with
            redemption values between $0.005 and $0.02 per point. The effective
            rebate is usually between 3–10%. Setting minimum redemption around
            500 points prevents customers from cashing in small amounts and
            keeps perceived value high.
          </p>
        </div>

        {/* How does earning work? */}
        <div data-id="element-10704" data-mp="fmmeo22ytl1k">
          <button type="button" onClick={() => setShowEarningExplanation(!showEarningExplanation)} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0EA5E9] hover:underline focus:outline-none" data-id="element-10705" data-mp="fmcr0l5e7pz1">
            <Info className="w-3.5 h-3.5" data-id="element-10706" data-mp="ksrm8p5sszp9" />
            How does earning work?
          </button>
          {showEarningExplanation && <div className="mt-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 space-y-3" data-id="element-10707" data-mp="bq9ww1uujqy1">
              <p className="text-sm text-[#475569] m-0 leading-relaxed" data-id="element-10708" data-mp="18g9d8ih8ntr6">
                <span className="font-semibold text-[#0F172A]" data-id="element-10709" data-mp="18idgqzb86qqx">
                  Earning Rate
                </span>{' '}
                is the points multiplier for every dollar spent on qualifying
                orders. Tier multipliers stack on top — a Sommelier member
                (1.25×) earning on the same ${exampleOrderValue} order would
                receive{' '}
                <span className="font-semibold text-[#0F172A]" data-id="element-10710" data-mp="1r8bo1d778fti">
                  {Math.round(exampleEarn * 1.25).toLocaleString()}
                </span>{' '}
                points instead of{' '}
                <span className="font-semibold text-[#0F172A]" data-id="element-10711" data-mp="16wfz6v87mx5x">
                  {exampleEarn.toLocaleString()}
                </span>
                . Per-product earning rules (configured later on the Rules page)
                can override the base rate for specific products or collections.
              </p>
              <p className="text-sm text-[#475569] m-0 leading-relaxed" data-id="element-10712" data-mp="1210lc3a7d19i">
                <span className="font-semibold text-[#0F172A]" data-id="element-10713" data-mp="1pptnwilby7d3">
                  Redemption Value
                </span>{' '}
                defines how much each point is worth when a customer redeems. A
                redemption value of $0.01/pt means 1,000 points convert to $10.
              </p>
              <p className="text-sm text-[#475569] m-0 leading-relaxed" data-id="element-10714" data-mp="1kw8ul3uom1k8">
                <span className="font-semibold text-[#0F172A]" data-id="element-10715" data-mp="28n4me0z50wrh">
                  Minimum Redemption
                </span>{' '}
                is the floor — customers can't redeem fewer points than this in
                a single transaction.
              </p>
            </div>}
        </div>
      </div>;
  };
  // --- Step 3: Tiers ---
  const handleSwitchToCustom = () => {
    setTierMode('custom');
    // Pre-populate with template tiers (already the default state)
    if (customTiers.length === 0) {
      setCustomTiers(TEMPLATE_TIERS);
    }
  };
  const handleAddTier = () => {
    if (customTiers.length >= 5) return;
    const newTier: CustomTier = {
      id: `tier-${Date.now()}`,
      name: `Tier ${customTiers.length + 1}`,
      color: SWATCH_COLORS[customTiers.length % SWATCH_COLORS.length].hex,
      threshold: 0,
      multiplier: 1,
      benefits: []
    };
    setCustomTiers([...customTiers, newTier]);
  };
  const handleDeleteTier = (id: string) => {
    setCustomTiers(customTiers.filter(t => t.id !== id));
  };
  const updateTier = (id: string, patch: Partial<CustomTier>) => {
    setCustomTiers(customTiers.map(t => t.id === id ? {
      ...t,
      ...patch
    } : t));
  };
  const addBenefit = (tierId: string) => {
    const draft = (benefitDrafts[tierId] || '').trim();
    if (!draft) return;
    const tier = customTiers.find(t => t.id === tierId);
    if (!tier) return;
    updateTier(tierId, {
      benefits: [...tier.benefits, draft]
    });
    setBenefitDrafts({
      ...benefitDrafts,
      [tierId]: ''
    });
  };
  const removeBenefit = (tierId: string, idx: number) => {
    const tier = customTiers.find(t => t.id === tierId);
    if (!tier) return;
    updateTier(tierId, {
      benefits: tier.benefits.filter((_, i) => i !== idx)
    });
  };
  const renderStep3 = () => <div className="max-w-[720px] mx-auto space-y-6" data-id="element-10716" data-mp="xntgla8zoadw">
      <div className="text-center" data-id="element-10717" data-mp="12gmjrtot7anv">
        <h2 className="text-2xl font-bold text-[#0F172A] m-0" data-id="element-10718" data-mp="12gw87cu7h9rg">
          Set up your tiers
        </h2>
        <p className="text-sm text-[#64748B] mt-2 m-0" data-id="element-10719" data-mp="507c7zrxt1pz">
          Tiers give your top customers status, perks, and a reason to keep
          buying. Choose a single tier for a flat points program.
        </p>
      </div>

      <>
          {/* Tier Configuration */}
          <section data-id="element-10720" data-mp="2j889eikroxo7">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-3" data-id="element-10721" data-mp="2j9t0fy7vhyhb">
              Tier Configuration
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-id="element-10722" data-mp="29aakrtyzwyv2">
              {([{
            id: 'template' as TierMode,
            title: 'Wine Template',
            desc: 'Four tiers with sensible defaults'
          }, {
            id: 'custom' as TierMode,
            title: 'Build my own',
            desc: 'Up to five tiers, fully configurable'
          }, {
            id: 'single' as TierMode,
            title: 'Single tier',
            desc: 'A flat points program, no status ladder'
          }] as const).map(opt => <button key={opt.id} type="button" onClick={() => opt.id === 'custom' ? handleSwitchToCustom() : setTierMode(opt.id)} className={`text-left rounded-lg border p-4 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/30 ${tierMode === opt.id ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1] hover:shadow-sm'}`} data-id="element-10723" data-mp="1ljocc1l2xckj">
                  <div className="text-sm font-semibold text-[#0F172A]" data-id="element-10724" data-mp="2vguwfql2xp18">
                    {opt.title}
                  </div>
                  <div className="text-xs text-[#64748B] mt-0.5" data-id="element-10725" data-mp="30itzt0gtkly6">{opt.desc}</div>
                </button>)}
            </div>

            {isSingleTierMode ? <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm space-y-3" data-id="element-10726" data-mp="2qite57vh9tqt">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-10727" data-mp="2cry1dkhuyshy">
                  <div data-id="element-10728" data-mp="2hq9crznsxb2a">
                    <label htmlFor="singleTierName" className="block text-[11px] text-[#94A3B8] uppercase tracking-wider mb-1" data-id="element-10729" data-mp="2hpgdtkv33pup">
                      Tier name
                    </label>
                    <input id="singleTierName" type="text" value={singleTier.name} onChange={e => setSingleTier({
                ...singleTier,
                name: e.target.value
              })} className="w-full h-9 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10730" data-mp="27slg346mxthh" />
                  </div>
                  <div data-id="element-10731" data-mp="1t5mpfbflr33t">
                    <label htmlFor="singleTierMultiplier" className="block text-[11px] text-[#94A3B8] uppercase tracking-wider mb-1" data-id="element-10732" data-mp="1t4bm1cq2jxd8">
                      Multiplier
                    </label>
                    <input id="singleTierMultiplier" type="number" step={0.05} min={0} value={singleTier.multiplier} onChange={e => setSingleTier({
                ...singleTier,
                multiplier: Number(e.target.value)
              })} className="w-full h-9 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10733" data-mp="234c6x33l6ljj" />
                  </div>
                </div>
                <p className="text-xs text-[#64748B] flex items-start gap-1.5 m-0" data-id="element-10734" data-mp="3n8qr913flmki">
                  <Info className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-10735" data-mp="3n6mncj5j8nk8" />
                  Every member sits in this tier, so the program runs as a flat
                  points program. You can add more tiers at any time from the
                  Rules page.
                </p>
              </div> : tierMode === 'template' ? <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-10736" data-mp="349l885i9l72k">
                <div className="p-5 border-b border-[#E2E8F0]" data-id="element-10737" data-mp="2zjei2183tei7">
                  <div className="text-base font-semibold text-[#0F172A]" data-id="element-10738" data-mp="2zh1v19nhodya">
                    Use the Wine Template
                  </div>
                  <div className="text-sm text-[#64748B] mt-1" data-id="element-10739" data-mp="2qizvkyjeltd4">
                    Sensible defaults for most wineries
                  </div>
                </div>
                <ul className="divide-y divide-[#E2E8F0] m-0 list-none p-0" data-id="element-10740" data-mp="2ll7vhh8lv0xu">
                  {TEMPLATE_TIERS.map(t => <li key={t.id} className="flex items-center gap-3 px-5 py-3" data-id="element-10741" data-mp="2gmmvnix9mj9x">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{
                background: t.color
              }} data-id="element-10742" data-mp="w4caxlw4gl64" />
                      <div className="flex-1 min-w-0" data-id="element-10743" data-mp="15zw4w2r4l9s8">
                        <div className="text-sm font-medium text-[#0F172A]" data-id="element-10744" data-mp="16175jtp9y1f4">
                          {t.name}
                        </div>
                        <div className="text-xs text-[#64748B]" data-id="element-10745" data-mp="dca2gxtmwb6y">
                          ${t.threshold.toLocaleString()} spend · {t.multiplier}×
                          points
                          {t.benefits.length > 0 && ` · ${t.benefits.join(' · ').toLowerCase()}`}
                        </div>
                      </div>
                    </li>)}
                </ul>
              </div> : <div className="space-y-3" data-id="element-10746" data-mp="3qqmu0i10te1l">
                <div className="flex items-center justify-end" data-id="element-10747" data-mp="3qpv9q3cvj2qm">
                  <div className="text-xs text-[#94A3B8]" data-id="element-10748" data-mp="3us02kzvhzvc7">
                    {customTiers.length} / 5 tiers
                  </div>
                </div>

                <div className="space-y-3" data-id="element-10749" data-mp="11vx5kcnusxno">
                  {customTiers.map(tier => <div key={tier.id} className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm" data-id="element-10750" data-mp="wxna00y7vv1j">
                      <div className="flex items-start gap-3" data-id="element-10751" data-mp="1bm9ok0g0sirp">
                        {/* Swatch picker */}
                        <div className="flex flex-col gap-1 flex-shrink-0" data-id="element-10752" data-mp="16ozx6f92lx18">
                          <span className="text-[11px] text-[#94A3B8] uppercase tracking-wider mb-0.5" data-id="element-10753" data-mp="izym0k8us7s9">
                            Colour
                          </span>
                          <div className="grid grid-cols-3 gap-1" data-id="element-10754" data-mp="rzbvhcwz8wcz">
                            {SWATCH_COLORS.map(s => <button key={s.hex} type="button" onClick={() => updateTier(tier.id, {
                      color: s.hex
                    })} aria-label={s.name} title={s.name} className={`w-5 h-5 rounded-full border-2 transition-all ${tier.color === s.hex ? 'border-[#0F172A] scale-110' : 'border-white hover:scale-105'}`} style={{
                      background: s.hex
                    }} data-id="element-10755" data-mp="ny8jj1i1vkh1" />)}
                          </div>
                        </div>

                        {/* Editable fields */}
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-10756" data-mp="1wza3kqz4zh77">
                          <div className="sm:col-span-3" data-id="element-10757" data-mp="21x1xtrn97fek">
                            <label className="block text-[11px] text-[#94A3B8] uppercase tracking-wider mb-1" data-id="element-10758" data-mp="21zo65thyxyxu">
                              Tier name
                            </label>
                            <input type="text" value={tier.name} onChange={e => updateTier(tier.id, {
                      name: e.target.value
                    })} className="w-full h-9 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10759" data-mp="2ulh9c476ffl5" />
                          </div>
                          <div data-id="element-10760" data-mp="3dds93p6jaxf1">
                            <label className="block text-[11px] text-[#94A3B8] uppercase tracking-wider mb-1" data-id="element-10761" data-mp="3de1pqm5ovs82">
                              Threshold {calcMethod === 'points' ? '(pts)' : '($)'}
                            </label>
                            <input type="number" min={0} value={tier.threshold} onChange={e => updateTier(tier.id, {
                      threshold: Number(e.target.value)
                    })} className="w-full h-9 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10762" data-mp="1fq9bm9sirw0d" />
                          </div>
                          <div data-id="element-10763" data-mp="23i6bd2ip2xl4">
                            <label className="block text-[11px] text-[#94A3B8] uppercase tracking-wider mb-1" data-id="element-10764" data-mp="1yobt83iqmqo5">
                              Multiplier
                            </label>
                            <input type="number" step={0.05} min={0} value={tier.multiplier} onChange={e => updateTier(tier.id, {
                      multiplier: Number(e.target.value)
                    })} className="w-full h-9 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10765" data-mp="2r5xxexu1eozl" />
                          </div>
                          <div data-id="element-10766" data-mp="33ngzmv0spc73">
                            <label className="block text-[11px] text-[#94A3B8] uppercase tracking-wider mb-1" data-id="element-10767" data-mp="33mff7zxw3dcp">
                              &nbsp;
                            </label>
                            <button type="button" onClick={() => handleDeleteTier(tier.id)} className="w-full h-9 inline-flex items-center justify-center gap-1.5 border border-[#FECACA] text-[#DC2626] text-xs font-medium rounded-md hover:bg-[#FEF2F2] transition-colors focus:outline-none focus:ring-2 focus:ring-[#FCA5A5]" data-id="element-10768" data-mp="3dl4urm2bclps">
                              <Trash2 className="w-3.5 h-3.5" data-id="element-10769" data-mp="3rct79qgh5c7a" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Benefits chips */}
                      <div className="mt-4" data-id="element-10770" data-mp="3booekoveyu2g">
                        <label className="block text-[11px] text-[#94A3B8] uppercase tracking-wider mb-1.5" data-id="element-10771" data-mp="3blac7gmr2kv0">
                          Benefits
                        </label>
                        <div className="flex flex-wrap items-center gap-1.5" data-id="element-10772" data-mp="3lezn6oq1lrfo">
                          {tier.benefits.map((b, idx) => <span key={idx} className="inline-flex items-center gap-1 bg-[#F1F5F9] text-[#475569] text-xs font-medium rounded-full pl-2.5 pr-1 py-1" data-id="element-10773" data-mp="3gkmyo09plpx2" data-mpi={idx}>
                              {b}
                              <button type="button" onClick={() => removeBenefit(tier.id, idx)} aria-label={`Remove ${b}`} className="hover:bg-[#E2E8F0] rounded-full p-0.5 text-[#94A3B8] hover:text-[#475569]" data-id="element-10774" data-mp="3ph4cjb9gk1wi">
                                <X className="w-3 h-3" data-id="element-10775" data-mp="35z2utgmd80n" />
                              </button>
                            </span>)}
                          <div className="inline-flex items-center gap-1" data-id="element-10776" data-mp="24zpw78irhtva">
                            <input type="text" value={benefitDrafts[tier.id] || ''} onChange={e => setBenefitDrafts({
                      ...benefitDrafts,
                      [tier.id]: e.target.value
                    })} onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addBenefit(tier.id);
                      }
                    }} placeholder="Add benefit…" className="h-7 px-2 border border-[#CBD5E1] rounded-full text-xs text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none w-32" data-id="element-10777" data-mp="kh5o7vnyk2ph" />
                            <button type="button" onClick={() => addBenefit(tier.id)} disabled={!benefitDrafts[tier.id]?.trim()} className="w-7 h-7 rounded-full bg-[#0EA5E9] text-white inline-flex items-center justify-center hover:bg-[#0284C7] disabled:bg-[#CBD5E1] disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/30" aria-label="Add benefit" data-id="element-10778" data-mp="3nye1s3bnkgee">
                              <Plus className="w-3.5 h-3.5" data-id="element-10779" data-mp="dyq2dkwt8wmc" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>

                <button type="button" onClick={handleAddTier} disabled={customTiers.length >= 5} className="w-full inline-flex items-center justify-center gap-1.5 border border-dashed border-[#CBD5E1] text-[#475569] text-sm font-medium rounded-lg py-3 hover:border-[#0EA5E9] hover:text-[#0EA5E9] hover:bg-[#F0F9FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-[#CBD5E1] disabled:hover:text-[#475569] disabled:hover:bg-transparent" data-id="element-10780" data-mp="55mgqpc0hdgm">
                  <Plus className="w-4 h-4" data-id="element-10781" data-mp="3de4jjwhcesgw" />
                  Add Tier
                </button>
              </div>}
          </section>

          {/* Calculation Method */}
          <section data-id="element-10782" data-mp="1gji6es0jl4m3">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-3" data-id="element-10783" data-mp="1bmhz6dryhvte">
              Calculation Method
            </h3>
            <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm divide-y divide-[#E2E8F0]" data-id="element-10784" data-mp="1j57u50a0to96">
              {CALC_METHODS.map(opt => <label key={opt.id} className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-[#F8FAFC] transition-colors" data-id="element-10785" data-mp="1o2r59rbf9kww">
                  <input type="radio" name="calcMethod" checked={calcMethod === opt.id} onChange={() => setCalcMethod(opt.id)} className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10786" data-mp="1xvf4sttse2zb" />
                  <div data-id="element-10787" data-mp="155zr1waqlvzp">
                    <span className="text-sm text-[#0F172A]" data-id="element-10788" data-mp="155gaaiwtpdn9">
                      {opt.label}
                      {opt.note && <span className="text-xs text-[#94A3B8] ml-1.5" data-id="element-10789" data-mp="1a49pqgvo7eq2">
                          ({opt.note})
                        </span>}
                    </span>
                    {opt.desc && <div className="text-xs text-[#64748B] mt-0.5" data-id="element-10790" data-mp="2ll9dcz72p5dr">
                        {opt.desc}
                      </div>}
                  </div>
                </label>)}
            </div>
          </section>

          {/* Qualification Window — Annual Spend only, and only with a ladder */}
          {calcMethod === 'annual' && !isSingleTierMode && <section data-id="element-10791" data-mp="2qhkwmx81q5ks">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-3" data-id="element-10792" data-mp="2vgfffz60ju4w">
                Qualification window
              </h3>
              <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm divide-y divide-[#E2E8F0]" data-id="element-10793" data-mp="2zisksmgulq15">
                {QUALIFICATION_WINDOWS.map(opt => <label key={opt.id} className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-[#F8FAFC] transition-colors" data-id="element-10794" data-mp="39g6zohn4i6rc">
                    <input type="radio" name="qualificationWindow" checked={qualificationWindow === opt.id} onChange={() => setQualificationWindow(opt.id)} className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10795" data-mp="3e7yho3s7r45v" />
                    <div className="flex-1" data-id="element-10796" data-mp="3n8wmgoah7fpi">
                      <div className="text-sm text-[#0F172A]" data-id="element-10797" data-mp="3s6ofxmqrr9vt">{opt.title}</div>
                      <div className="text-xs text-[#64748B] mt-0.5" data-id="element-10798" data-mp="3s4ls99d39su9">
                        {opt.desc}
                      </div>
                      {opt.id === 'custom' && qualificationWindow === 'custom' && <div className="mt-2" data-id="element-10799" data-mp="1bix87utn28cf">
                          <label className="block text-[11px] text-[#94A3B8] uppercase tracking-wider mb-1" htmlFor="custom-start-month" data-id="element-10800" data-mp="17i4yl0guu1fm">
                            Window starts
                          </label>
                          <div className="relative w-[200px]" data-id="element-10801" data-mp="1z8rj46uedmu1">
                            <select id="custom-start-month" value={customStartMonth} onChange={e => setCustomStartMonth(e.target.value)} className="w-full h-9 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10802" data-mp="1v87ll25wwh7d">
                              {MONTHS.map(month => <option key={month} value={month} data-id="element-10803" data-mp="2iyleeuwbmk30">
                                  {month}
                                </option>)}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10804" data-mp="2x4detfdthw85" />
                          </div>
                        </div>}
                    </div>
                  </label>)}
              </div>
              <div className="mt-2 text-xs text-[#64748B] flex items-start gap-1.5" data-id="element-10805" data-mp="2scdkc2ginviy">
                <Info className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-10806" data-mp="2ecubf7fvsg5l" />
                <div className="space-y-1" data-id="element-10807" data-mp="2eexbylentrok">
                  <p className="m-0" data-id="element-10808" data-mp="2jc78el0gv3lw">{windowExplainer}</p>
                  {isFixedWindow && <>
                      <p className="m-0" data-id="element-10809" data-mp="25cxujrzqgll3">
                        Spend in one period sets the tier the member holds
                        through the whole of the next period.
                      </p>
                      <p className="m-0" data-id="element-10810" data-mp="3luh6uygkswu">
                        A member who joins partway through a period has until
                        the end of the following period to qualify.
                      </p>
                    </>}
                </div>
              </div>
            </section>}

          {/* Downgrade Policy — needs somewhere to move up or down to */}
          {!isSingleTierMode && <section data-id="element-10811" data-mp="21ui5ki5gnjk6">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-3" data-id="element-10812" data-mp="21v0bk6hxdc5a">
              Downgrade Policy
            </h3>
            <p className="text-xs text-[#64748B] mb-3 m-0" data-id="element-10813" data-mp="2bsefx7qkqah8">
              Members move up as soon as they qualify. Tier is only reviewed for
              downgrade {isFixedWindow ? 'at period close' : 'on the chosen review frequency'}.
            </p>
            <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm divide-y divide-[#E2E8F0]" data-id="element-10814" data-mp="2krt7kaih8r3b">
              <label className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-[#F8FAFC] transition-colors" data-id="element-10815" data-mp="2fuj77ztjuv0p">
                <input type="radio" name="downgrade" checked={downgradePolicy === 'permanent'} onChange={() => setDowngradePolicy('permanent')} className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10816" data-mp="qh6248dm52yj" />
                <div data-id="element-10817" data-mp="nwq7eaw4r1b8">
                  <div className="text-sm text-[#0F172A]" data-id="element-10818" data-mp="rvywh2g86b0g">
                    Once earned, permanent
                  </div>
                  <div className="text-xs text-[#64748B] mt-0.5" data-id="element-10819" data-mp="wv20cjkgyb6u">
                    Recommended for wine clubs
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-[#F8FAFC] transition-colors" data-id="element-10820" data-mp="46xspsic7o1i">
                <input type="radio" name="downgrade" checked={downgradePolicy === 'reevaluate'} onChange={() => setDowngradePolicy('reevaluate')} className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10821" data-mp="96sn461bmvrb" />
                <div className="flex-1" data-id="element-10822" data-mp="iwlxj3pq94tz">
                  <div className="text-sm text-[#0F172A]" data-id="element-10823" data-mp="ixnbbfy4o5fe">
                    Re-evaluate periodically
                  </div>
                  <div className="text-xs text-[#64748B] mt-0.5" data-id="element-10824" data-mp="3mkdwbh44fxdm">
                    Members can move down tiers if their spend drops
                  </div>
                </div>
              </label>
            </div>

            {downgradePolicy === 'reevaluate' && <div className="mt-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4" data-id="element-10825" data-mp="3a2tar6hulc36">
                {isFixedWindow ? <p className="text-xs text-[#64748B] m-0" data-id="element-10826" data-mp="351419va9x6h7">
                    Tier is reviewed for downgrade at period close.
                  </p> : <>
                    <div className="text-xs font-medium text-[#475569] mb-2" data-id="element-10827" data-mp="2w2im8a40g9l6">
                      How often tiers are reviewed for downgrade
                    </div>
                    <div className="space-y-2" data-id="element-10828" data-mp="2mcgxdg3u2n57">
                      {[{
                id: 'daily' as EvalCadence,
                label: 'Daily'
              }, {
                id: 'monthly' as EvalCadence,
                label: 'Monthly'
              }].map(opt => <label key={opt.id} className="flex items-center gap-2 cursor-pointer" data-id="element-10829" data-mp="2cdrna9i6i6zi">
                          <input type="radio" name="cadence" checked={evalCadence === opt.id} onChange={() => setEvalCadence(opt.id)} className="text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10830" data-mp="28d7qx85fj7ef" />
                          <span className="text-sm text-[#0F172A]" data-id="element-10831" data-mp="22rqc2lkrtwjn">
                            {opt.label}
                          </span>
                        </label>)}
                    </div>
                    <p className="mt-3 text-xs text-[#64748B] flex items-start gap-1.5 m-0" data-id="element-10832" data-mp="1tr0k5symsqb7">
                      <Info className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-10833" data-mp="2hfbj2qkrofwu" />
                      Daily and Monthly evaluation runs as a scheduled job.
                      Members are notified via email if they move tiers (if
                      Klaviyo enabled).
                    </p>
                  </>}
              </div>}
          </section>}
        </>
    </div>;
  // --- Step 4: Review ---
  const tiersSummary = (() => {
    if (tierMode === 'template') return '4 tiers (Wine Template)';
    if (isSingleTierMode) return `1 tier — ${singleTier.name || 'Unnamed'} (flat program)`;
    return `${customTiers.length} custom tier${customTiers.length === 1 ? '' : 's'}`;
  })();
  const audienceLabel = AUDIENCE_CARDS.find(card => card.id === audience)?.title ?? '';
  const yourSettingsRows: {
    label: string;
    value: string;
    goTo: Step;
  }[] = [{
    label: 'Program Name',
    value: programName || 'Acme Wine Rewards',
    goTo: 1
  }, {
    label: 'Audience',
    value: audienceLabel,
    goTo: 1
  }, {
    label: 'Earning Rate',
    value: `${pointsPerDollar} points per $1 spent`,
    goTo: 2
  }, {
    label: 'Redemption Value',
    value: `${redemptionValue.toFixed(2)} per point`,
    goTo: 2
  }, {
    label: 'Minimum Redemption',
    value: `${minRedemption.toLocaleString()} points`,
    goTo: 2
  }, {
    label: 'Tiers',
    value: tiersSummary,
    goTo: 3
  }, ...(calcMethod === 'annual' ? [{
    label: 'Qualification Window',
    value: qualificationWindow === 'custom' ? `${windowLabel} (starts 1 ${customStartMonth})` : windowLabel,
    goTo: 3 as Step
  }] : [])];
  const defaultsRows: {
    label: string;
    value: string;
    tooltip: string;
  }[] = [{
    label: 'Bonus Events',
    value: 'Signup (500 pts) and Birthday (200 pts) enabled. Referrals, reviews, milestones disabled by default.',
    tooltip: 'Configure from the Rules page after setup'
  }, {
    label: 'Points Expiration',
    value: 'Off',
    tooltip: 'Configure from Program Settings after setup'
  }, {
    label: 'Klaviyo Events',
    value: 'Enabled with default event toggles (requires Klaviyo connection in Integrations)',
    tooltip: 'Configure from Program Settings after setup'
  }, {
    label: 'Effective Rebate',
    value: `${baseRebate.toFixed(1)}% base · ${topTierRebate.toFixed(1)}% on the top tier (${topMultiplier}× multiplier)`,
    tooltip: 'Adjust earning rate on step 2 or tier multipliers on step 3'
  }];
  const renderStep4 = () => <div className="max-w-[640px] mx-auto space-y-6" data-id="element-10834" data-mp="1clvyp7b3wv8j">
      <div className="text-center" data-id="element-10835" data-mp="1cmnt4esywmx8">
        <h2 className="text-2xl font-bold text-[#0F172A] m-0" data-id="element-10836" data-mp="152ms1ltzjan6">
          Almost there — let's review
        </h2>
        <p className="text-sm text-[#64748B] mt-2 m-0" data-id="element-10837" data-mp="10bwkq3200xpu">
          Confirm your setup. You can click any row to jump back and edit.
        </p>
      </div>

      {isRebateHigh && <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-4 flex items-start gap-3" data-id="element-10838" data-mp="qe0dhmc6mmx0">
          <Lightbulb className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-10839" data-mp="1o1qu2an4045c" />
          <p className="text-sm text-[#92400E] m-0" data-id="element-10840" data-mp="1nzo6dx9fin3s">
            At {pointsPerDollar} points per $1, ${redemptionValue.toFixed(2)}{' '}
            per point and a {topMultiplier}× top tier multiplier, you're giving
            back {topTierRebate.toFixed(1)}% of order value to your best
            customers. Worth checking that against your wine margins before you
            launch.
          </p>
        </div>}

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-10841" data-mp="3us89purnghtu">
        {/* Section 1: Your settings */}
        <div className="px-4 pt-4 pb-2" data-id="element-10842" data-mp="2tkmg6wpn2s0k">
          <h4 className="text-[11px] font-semibold text-[#475569] uppercase tracking-wider m-0" data-id="element-10843" data-mp="2thi65ipht21z">
            Your settings
          </h4>
        </div>
        <div className="divide-y divide-[#E2E8F0]" data-id="element-10844" data-mp="339nsocwz6r9s">
          {yourSettingsRows.map(row => <button key={row.label} type="button" onClick={() => setCurrentStep(row.goTo)} className="group w-full text-left flex items-center justify-between gap-4 px-4 py-3.5 hover:bg-[#F8FAFC] transition-colors focus:outline-none focus:bg-[#F0F9FF]" data-id="element-10845" data-mp="2yixmwykn0mek">
              <div className="flex-1 min-w-0" data-id="element-10846" data-mp="3887jorqfet90">
                <div className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider mb-1" data-id="element-10847" data-mp="3m8s0jics6ezv">
                  {row.label}
                </div>
                <div className="text-sm text-[#0F172A] truncate" data-id="element-10848" data-mp="3h9ykpja0ubwj">
                  {row.value}
                </div>
              </div>
              <Pencil className="w-4 h-4 text-[#94A3B8] opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity flex-shrink-0" data-id="element-10849" data-mp="1mve7tsrxzknp" />
            </button>)}
        </div>

        {/* Section divider */}
        <div className="border-t border-[#E2E8F0]" data-id="element-10850" data-mp="7cg9ygho6tmh" />

        {/* Section 2: Defaults applied */}
        <div className="px-4 pt-4 pb-2 bg-[#FAFAFB]" data-id="element-10851" data-mp="3otgaheduzy1j">
          <h4 className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider m-0" data-id="element-10852" data-mp="3oqlp9kn0u972">
            Defaults applied
          </h4>
          <p className="text-[11px] text-[#94A3B8] mt-0.5 m-0" data-id="element-10853" data-mp="3frgp69vta7ss">
            Configurable after setup
          </p>
        </div>
        <div className="divide-y divide-[#E2E8F0] bg-[#FAFAFB]" data-id="element-10854" data-mp="3krupf5r68dg9">
          {defaultsRows.map(row => <div key={row.label} className="group w-full flex items-start justify-between gap-4 px-4 py-3.5" data-id="element-10855" data-mp="35uvjrnfvquof">
              <div className="flex-1 min-w-0" data-id="element-10856" data-mp="3atf9lyxlgqm2">
                <div className="text-xs font-medium text-[#CBD5E1] uppercase tracking-wider mb-1" data-id="element-10857" data-mp="142p8hyiv7zar">
                  {row.label}
                </div>
                <div className="text-sm text-[#64748B]" data-id="element-10858" data-mp="1910j4bgzidu1">{row.value}</div>
              </div>
              <div className="relative flex-shrink-0 mt-0.5" data-id="element-10859" data-mp="17l672u2upl1f">
                <Info className="w-3.5 h-3.5 text-[#CBD5E1] cursor-help peer" data-id="element-10860" data-mp="12ljxqvq58oxw" />
                <div role="tooltip" className="absolute right-0 top-5 z-10 hidden peer-hover:block bg-[#0F172A] text-white text-[11px] rounded-md px-2 py-1 whitespace-nowrap shadow-md" data-id="element-10861" data-mp="xm79c6jmj9t7">
                  {row.tooltip}
                </div>
              </div>
            </div>)}
        </div>
      </div>

      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-5" data-id="element-10862" data-mp="1lbrsoqjow930">
        <h4 className="text-sm font-semibold text-[#0F172A] mb-3 m-0" data-id="element-10863" data-mp="1ldutm5mdrmmi">
          What happens after setup:
        </h4>
        <ul className="space-y-2 m-0 list-none p-0" data-id="element-10864" data-mp="1cjg4lbc1sxq7">
          {['Your loyalty program will be created in Test Mode', "Tag customers with 'loyalty-test' in Shopify to pilot", 'Build out your reward catalog from the Rewards page', 'When you\'re ready, click "Go Live" from the Overview'].map(item => <li key={item} className="flex items-start gap-2 text-sm text-[#475569]" data-id="element-10865" data-mp="248t16lcgcrbp">
              <Check className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-10866" data-mp="3mzgpmz188dg8" />
              <span data-id="element-10867" data-mp="3rwqkiu7dxhbg">{item}</span>
            </li>)}
        </ul>
      </div>
    </div>;
  // --- Step 5: Launch ---
  const renderStep5 = () => {
    const cards: {
      id: 'test' | 'live';
      title: string;
      recommended?: boolean;
    }[] = [{
      id: 'test',
      title: 'Start in Test Mode',
      recommended: true
    }, {
      id: 'live',
      title: 'Go Live now'
    }];
    return <div className="max-w-[640px] mx-auto space-y-6" data-id="element-10868" data-mp="eo43k3ombjo4">
        <div className="text-center" data-id="element-10869" data-mp="jgf0qyrz4rcz">
          <h2 className="text-2xl font-bold text-[#0F172A] m-0" data-id="element-10870" data-mp="jhgp24y03acn">
            How do you want to launch?
          </h2>
        </div>

        <div className="space-y-3" data-id="element-10871" data-mp="shda7wl6lr2y">
          {cards.map(card => {
          const isSelected = launchMode === card.id;
          return <button key={card.id} type="button" onClick={() => setLaunchMode(card.id)} className={`w-full text-left rounded-lg border p-5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/30 ${isSelected ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1] hover:shadow-sm'}`} data-id="element-10872" data-mp="xfn5s8atitp3">
                <div className="flex items-start gap-3" data-id="element-10873" data-mp="okfur4ae6cgv">
                  <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'border-[#0EA5E9] bg-[#0EA5E9]' : 'border-[#CBD5E1] bg-white'}`} data-id="element-10874" data-mp="oky14tqrq72i">
                    {isSelected && <div className="w-2 h-2 rounded-full bg-white" data-id="element-10875" data-mp="3s3ixj2ge54fo" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-10876" data-mp="aqokftou6ok3">
                    <div className="flex items-center gap-2 mb-2" data-id="element-10877" data-mp="5swrqxgdayeu">
                      <h3 className="text-base font-semibold text-[#0F172A] m-0" data-id="element-10878" data-mp="1m7y1yaoen6ir">
                        {card.title}
                      </h3>
                      {card.recommended && <span className="px-2 py-0.5 text-[11px] font-semibold text-[#0369A1] bg-[#E0F2FE] rounded-full" data-id="element-10879" data-mp="1hb7ntsw5ukzz">
                          Recommended
                        </span>}
                    </div>
                    {card.id === 'test' ? <div className="space-y-2" data-id="element-10880" data-mp="1fs7q768lx8ge">
                        <p className="text-sm text-[#475569] m-0" data-id="element-10881" data-mp="1ftr44q6sj7f0">
                          Only customers tagged 'loyalty-test' will participate.
                          No Klaviyo emails, no Shopify tags, no wallet passes
                          will fire. You can pilot the program with a few VIPs
                          first.
                        </p>
                        <p className="text-sm text-[#475569] m-0" data-id="element-10882" data-mp="1yrliljqoub4s">
                          Tag 1-5 test customers in Shopify, then visit the
                          Members page to see them participate. When ready,
                          click 'Go Live' from the Overview page.
                        </p>
                      </div> : <div className="space-y-2" data-id="element-10883" data-mp="2va9fsd5b9p9r">
                        <p className="text-sm text-[#475569] m-0" data-id="element-10884" data-mp="354f0xft1x3aa">
                          All eligible customers participate immediately.
                          Klaviyo, Shopify tags, and wallet passes (if enabled)
                          activate now.
                        </p>
                        <div className="flex items-start gap-2 bg-[#FEF3C7] border border-[#FDE68A] rounded-md p-3 mt-1" data-id="element-10885" data-mp="2r4w5nnlc9mfe">
                          <AlertTriangle className="w-4 h-4 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-10886" data-mp="2m6cjpn6qwan1" />
                          <p className="text-xs text-[#92400E] m-0" data-id="element-10887" data-mp="2heknox4tlcpi">
                            We strongly recommend Test Mode for your first time.
                            You can switch to Live at any moment.
                          </p>
                        </div>
                      </div>}
                  </div>
                </div>
              </button>;
        })}
        </div>
      </div>;
  };
  return <div className="bg-[#F1F5F9] min-h-full flex flex-col" data-id="element-10888" data-mp="1yfyndkh4npmn">
      {/* Breadcrumb header */}
      <div className="px-6 pt-6" data-id="element-10889" data-mp="1od32uixsdvhr">
        <NavBar tabs={[]} title="Loyalty & Rewards › Setup" data-id="element-10890" data-mp="1obrx4dks6do0" />
      </div>

      {/* Resume banner */}
      {isResumed && <div className="mx-6 mt-4 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg px-4 py-3 flex items-center justify-between gap-4" data-id="element-10891" data-mp="1tdhd87mv1hiy">
          <div className="flex items-center gap-2 text-sm text-[#1E40AF]" data-id="element-10892" data-mp="239apdpwo3907">
            <Info className="w-4 h-4 flex-shrink-0" data-id="element-10893" data-mp="2377xf0byf5su" />
            <span data-id="element-10894" data-mp="15n4vk51tc6f6">Resuming your saved draft.</span>
          </div>
          <button type="button" onClick={handleStartOver} className="text-sm font-medium text-[#1D4ED8] hover:underline focus:outline-none" data-id="element-10895" data-mp="1akn4hvftyln6">
            Start over
          </button>
        </div>}

      {/* Stepper */}
      <div className={`bg-white border-y border-[#E2E8F0] px-6 py-5 ${isResumed ? 'mt-4' : 'mt-6'}`} data-id="element-10896" data-mp="1kay9llb930vn">
        {renderStepper()}
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 pb-28" data-id="element-10897" data-mp="ruf8dxajsvgh">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderStep5()}
      </div>

      {/* Sticky Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 md:left-[240px] bg-white border-t border-[#E2E8F0] px-6 py-4 flex items-center justify-between z-20 shadow-[0_-1px_2px_rgba(0,0,0,0.04)]" data-id="element-10898" data-mp="2fyrtwyt2gqdc">
        <div className="flex items-center gap-3" data-id="element-10899" data-mp="2ozhq43m8oirl">
          {currentStep > 1 && <Button variant="ghost" onClick={handleBack} data-id="element-10900" data-mp="2oxn3mkvbkile">
              ← Back
            </Button>}
        </div>

        <div className="flex items-center gap-3" data-id="element-10901" data-mp="2kwbjuvz4mrl4">
          <Button variant="secondary" onClick={handleSaveDraft} data-id="element-10902" data-mp="2yohszy3v3yb2">
            Save Draft
          </Button>
          <Button variant="ghost" onClick={() => setShowCancelConfirm(true)} data-id="element-10903" data-mp="2trra730oaoly">
            Cancel
          </Button>
          {currentStep < 5 ? <Button variant="primary" onClick={handleNext} disabled={!canProceed()} iconRight={<ChevronRight className="w-4 h-4" data-id="element-10905" data-mp="33qguf2g1kl5d" />} data-id="element-10904" data-mp="38lcn84t3rcjj">
              Next
            </Button> : <Button variant="primary" onClick={handleCreateProgram} data-id="element-10906" data-mp="3nbqr0uu911vz">
              Create Program
            </Button>}
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowCancelConfirm(false)} data-id="element-10907" data-mp="3dc00f48t7716" />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-10908" data-mp="3idfiqojlqzfv">
            <div className="bg-white rounded-lg shadow-xl w-[420px] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-10909" data-mp="33l5anpwctpaz">
              <div className="p-6" data-id="element-10910" data-mp="38eztys81s29j">
                <h3 className="text-base font-semibold text-[#0F172A] mb-2 m-0" data-id="element-10911" data-mp="2umjkp9ojz806">
                  Discard setup progress?
                </h3>
                <p className="text-sm text-[#475569] m-0" data-id="element-10912" data-mp="2zl3axma6j5yc">
                  Your changes won't be saved.
                </p>
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-3" data-id="element-10913" data-mp="2pn6x2mpuxwwj">
                <Button variant="ghost" size="sm" onClick={() => setShowCancelConfirm(false)} data-id="element-10914" data-mp="2oq8hrhd44glr">
                  Cancel
                </Button>
                <Button variant="danger" size="sm" onClick={handleDiscard} data-id="element-10915" data-mp="2toa368y0gs0e">
                  Discard
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Toast */}
      {toast && <Toast id={toast.id} type="success" title={toast.title} duration={4000} onClose={() => setToast(null)} data-id="element-10916" data-mp="37hrn3uum06qy" />}
    </div>;
}